import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { CalculatorClient } from './api/client';
import {
  ApiCallError,
  type ComputeResponse,
  type EventosInputs,
  type EventosOrgInputs,
  type EventosOrgSubmitterMeta,
  type InstanceOfferingDetail,
  type SchemaResponse,
  type SubmitterInput,
} from './api/types';

// i18n moved to ./lib/i18n.ts pra evitar circular import com os steps org_*
// que dependem de `t()`. Re-export aqui mantém os call sites existentes.
export { t, I18N_FALLBACK_PTBR, type I18nBundle } from './lib/i18n';
import { t } from './lib/i18n';
import { CheckoutConfirm } from './components/CheckoutConfirm';
import { CheckoutIframeModal } from './components/CheckoutIframeModal';
import { ResultView } from './components/ResultView';
import { StepDeslocamento } from './components/StepDeslocamento';
import { StepHospedagem } from './components/StepHospedagem';
import { StepOrgDeslocamento } from './components/StepOrgDeslocamento';
import { StepOrgEquipePublico } from './components/StepOrgEquipePublico';
import { StepOrgInfoEvento } from './components/StepOrgInfoEvento';
import { StepOrgOutros } from './components/StepOrgOutros';
import { WidgetShell } from './components/WidgetShell';
import { clearDraft, readDraft, writeDraft } from './lib/storage';

export interface AppProps {
  token: string;
  apiBase: string;
  locale?: string;
  /**
   * Quando true, o WidgetShell sabe que está em standalone full-screen
   * (parent já cobriu a viewport com bg image) e evita duplicar.
   */
  fullScreen?: boolean;
}

type Phase =
  | { kind: 'booting' }
  | { kind: 'unavailable'; message: string }
  // Fluxo participante (eventos-participante-v1.0.0) — 2 steps
  | { kind: 'step_1' }
  | { kind: 'step_2' }
  // Fluxo organização (eventos-organizacao-v1.0.0) — 4 steps + result
  | { kind: 'org_step_1' }
  | { kind: 'org_step_2' }
  | { kind: 'org_step_3' }
  | { kind: 'org_step_4' }
  // Comum
  | { kind: 'computing' }
  | { kind: 'result' }
  | { kind: 'checkout_confirm'; offer: InstanceOfferingDetail }
  | { kind: 'checkout_iframe'; sessionUrl: string; orderId: string; tco2e: number; offer: InstanceOfferingDetail }
  | { kind: 'checkout_paid'; orderId: string; tco2e: number; offer: InstanceOfferingDetail }
  | { kind: 'checkout_redirecting' };

const EMPTY_INPUTS: EventosInputs = {
  transport_mode: 'aviao',
  origin_city: '',
  distance_km: 0,
  round_trip: true,
  event_days: 1,
  lodging_nights: 0,
  lodging_type: 'nao_me_hospedei',
  intra_city_transport: 'a_pe',
  intra_city_km_per_day: 0,
};

const EMPTY_ORG_INPUTS: EventosOrgInputs = {
  crew_count: 0,
  venue_capacity: 0,
  audience_estimated: 0,
  electricity_paid: false,
  grid_consumption: { value: 0, unit: 'kWh' },
  generator_fuel: { value: 0, unit: 'L' },
  internal_fuel: { value: 0, unit: 'BRL' },
  employee_private_fuel: { value: 0, unit: 'BRL' },
  employee_public_fuel: { value: 0, unit: 'BRL' },
  consider_audience: false,
  audience_avg_km: 0,
  pct_public: 0,
  pct_private: 0,
  pct_walking: 0,
  pct_short_flight: 0,
  pct_medium_flight: 0,
  pct_long_flight: 0,
  consider_suppliers: false,
  suppliers: [],
  duration_hours: 0,
  glp_kg: 0,
  waste_organic_kg: 0,
  waste_recyclable_kg: 0,
};

const EMPTY_ORG_META: EventosOrgSubmitterMeta = {
  event_name: '',
  segment: '',
  cpf_cnpj: '',
  state: '',
  city: '',
  contact_name: '',
  contact_phone: '',
  contact_email: '',
};

function draftKey(token: string): string {
  return `oma-calc:${token}:draft`;
}

function orgDraftKey(token: string): string {
  return `oma-calc:${token}:org-draft`;
}

function orgMetaKey(token: string): string {
  return `oma-calc:${token}:org-meta`;
}

// pageWasReloaded é true quando o user fez F5 / Ctrl+R / Ctrl+Shift+R
// (em vez de navegação normal). Avaliado uma única vez ao carregar o
// módulo. Usado pra começar o widget com form limpo no reload — comum
// em uso de quiosque/PWA pra eventos onde cada pessoa quer começar do
// zero. sessionStorage por design persiste através de reloads.
const pageWasReloaded: boolean = (() => {
  try {
    if (typeof performance === 'undefined') return false;
    const entries = performance.getEntriesByType('navigation');
    const nav = entries[0] as PerformanceNavigationTiming | undefined;
    return nav?.type === 'reload';
  } catch {
    return false;
  }
})();

// loadDraftOrEmpty centraliza o readDraft com fallback pra EMPTY e
// honra pageWasReloaded — limpa o draft persistido e devolve EMPTY
// quando o boot atual veio de um reload.
function loadDraftOrEmpty<T>(key: string, empty: T): T {
  if (pageWasReloaded) {
    clearDraft(key);
    return empty;
  }
  return readDraft<T>(key) ?? empty;
}

export function App({ token, apiBase, locale, fullScreen }: AppProps) {
  const client = useMemo(() => new CalculatorClient({ baseUrl: apiBase, locale }), [apiBase, locale]);

  const [schema, setSchema] = useState<SchemaResponse | null>(null);
  const [phase, setPhase] = useState<Phase>({ kind: 'booting' });
  const [inputs, setInputs] = useState<EventosInputs>(
    () => loadDraftOrEmpty(draftKey(token), EMPTY_INPUTS),
  );
  const [orgInputs, setOrgInputs] = useState<EventosOrgInputs>(
    () => loadDraftOrEmpty(orgDraftKey(token), EMPTY_ORG_INPUTS),
  );
  const [orgMeta, setOrgMeta] = useState<EventosOrgSubmitterMeta>(
    () => loadDraftOrEmpty(orgMetaKey(token), EMPTY_ORG_META),
  );
  const [submitter, setSubmitter] = useState<SubmitterInput | undefined>();
  const [result, setResult] = useState<ComputeResponse | null>(null);
  const [errors, setErrors] = useState<Partial<Record<keyof EventosInputs, string>>>({});
  const [globalError, setGlobalError] = useState<string | undefined>();
  /**
   * i18n strings carregadas do backend no boot. Todas as mensagens
   * user-facing devem vir daqui (chaves `errors.*`, `consent_label`,
   * `privacy_link`) — frontend não hardcoda. Vide `feedback_oma_erros_via_backend`.
   */
  const [i18n, setI18n] = useState<Record<string, string>>();
  const abortRef = useRef<AbortController | null>(null);

  // Boot: fetch schema; após sucesso dispara i18n bundle (não bloqueante).
  // Ordem sequencial pra evitar race em testes/cache HTTP — i18n não muda
  // o phase, só recheia as strings.
  useEffect(() => {
    let cancelled = false;
    const ctrl = new AbortController();
    const effLocale = locale ?? 'pt-BR';

    client
      .getSchema(token, ctrl.signal)
      .then((s) => {
        if (cancelled) return;
        setSchema(s);
        // Dispatch fluxo inicial baseado em audience. Organizacao usa 4-step
        // wizard (mockup 20); participante usa 2-step (mockup 15).
        setPhase({ kind: s.audience === 'organizacao' ? 'org_step_1' : 'step_1' });
        // i18n é best-effort, dispara em background após schema OK.
        client.i18nBundle(effLocale, ctrl.signal)
          .then((b) => { if (!cancelled) setI18n(b.keys); })
          .catch(() => { /* fallback kicks in */ });
      })
      .catch((err) => {
        if (cancelled || err.name === 'AbortError') return;
        const msg =
          err instanceof ApiCallError && err.status === 404
            ? t(i18n, 'errors.unavailable')
            : err instanceof ApiCallError
              ? err.message // backend já mandou em PT-BR via envelope.error.message
              : t(i18n, 'errors.network_error');
        setPhase({ kind: 'unavailable', message: msg });
      });
    return () => {
      cancelled = true;
      ctrl.abort();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intencional: i18n só
    // serve aqui pra reagir ao boot inicial, não pra re-trigger.
  }, [client, token, locale]);

  // Persist drafts (participante e organização separados — não compartilham
  // shape).
  useEffect(() => {
    writeDraft(draftKey(token), inputs);
  }, [inputs, token]);

  useEffect(() => {
    writeDraft(orgDraftKey(token), orgInputs);
  }, [orgInputs, token]);

  useEffect(() => {
    writeDraft(orgMetaKey(token), orgMeta);
  }, [orgMeta, token]);

  const patchInputs = useCallback((patch: Partial<EventosInputs>) => {
    setInputs((prev) => ({ ...prev, ...patch }));
    setErrors({});
  }, []);

  // resetAll zera todo o estado pra iniciar uma nova sessão limpa:
  // inputs (participante e organização), submitter, result, errors, e
  // remove os drafts persistidos no sessionStorage. Usado em "Recalcular"
  // e no auto-reset após sucesso (UX de quiosque em eventos).
  const resetAll = useCallback(() => {
    setInputs(EMPTY_INPUTS);
    setOrgInputs(EMPTY_ORG_INPUTS);
    setOrgMeta(EMPTY_ORG_META);
    setSubmitter(undefined);
    setResult(null);
    setErrors({});
    setGlobalError(undefined);
    clearDraft(draftKey(token));
    clearDraft(orgDraftKey(token));
    clearDraft(orgMetaKey(token));
    setPhase({ kind: schema?.audience === 'organizacao' ? 'org_step_1' : 'step_1' });
  }, [schema, token]);

  const patchOrgInputs = useCallback((patch: Partial<EventosOrgInputs>) => {
    setOrgInputs((prev) => ({ ...prev, ...patch }));
  }, []);

  const patchOrgMeta = useCallback((patch: Partial<EventosOrgSubmitterMeta>) => {
    setOrgMeta((prev) => ({ ...prev, ...patch }));
  }, []);

  const validateStep1 = useCallback((): boolean => {
    const e: Partial<Record<keyof EventosInputs, string>> = {};
    if (!inputs.transport_mode) e.transport_mode = t(i18n, 'errors.distance_required');
    if (inputs.transport_mode === 'carro' && !inputs.car_fuel) {
      e.car_fuel = t(i18n, 'errors.fuel_required');
    }
    if (inputs.distance_km < 0 || Number.isNaN(inputs.distance_km)) {
      e.distance_km = t(i18n, 'errors.distance_required');
    }
    setErrors(e);
    return Object.keys(e).length === 0;
  }, [inputs, i18n]);

  const validateStep2 = useCallback((): boolean => {
    const e: Partial<Record<keyof EventosInputs, string>> = {};
    if (inputs.event_days < 1) e.event_days = t(i18n, 'errors.event_days_min');
    if (inputs.lodging_nights < 0) e.lodging_nights = t(i18n, 'errors.validation');
    if (!inputs.lodging_type) e.lodging_type = t(i18n, 'errors.validation');
    if (inputs.intra_city_transport === 'carro_proprio' && !inputs.intra_city_car_fuel) {
      e.intra_city_car_fuel = t(i18n, 'errors.fuel_required');
    }
    setErrors(e);
    return Object.keys(e).length === 0;
  }, [inputs, i18n]);

  // Compute happy path SEM submitter. Mockup 15 — coleta de email virou
  // opt-in inline no resultado (Step Submitter foi descontinuado).
  const runCompute = useCallback(async () => {
    if (!schema) return;
    setPhase({ kind: 'computing' });
    setGlobalError(undefined);
    abortRef.current?.abort();
    const ctrl = new AbortController();
    abortRef.current = ctrl;
    try {
      const resp = await client.compute(
        token,
        { inputs, locale: locale ?? schema.locale_default },
        ctrl.signal,
      );
      setResult(resp);
      setPhase({ kind: 'result' });
    } catch (err) {
      if ((err as Error).name === 'AbortError') return;
      const msg =
        err instanceof ApiCallError
          ? err.code === 'validation'
            ? t(i18n, 'errors.validation')
            : err.message // backend já mandou em PT-BR via envelope
          : t(i18n, 'errors.network_error');
      setGlobalError(msg);
      // Volta pro Step 2 (em vez do Step Submitter extinto) pra user corrigir.
      setPhase({ kind: 'step_2' });
    }
  }, [client, inputs, locale, schema, token, i18n]);

  // Compute do fluxo organizacao. Diferente do participante: o submitter é
  // *obrigatório* (lead capture acontece NA etapa 4, antes do compute), e
  // segue como `consent=true` implícito porque o B2B é vendable contact —
  // o cliente está submetendo o evento dele. Backend `consent` aqui é
  // legítimo: o organizador concorda com contato comercial.
  const runOrgCompute = useCallback(async () => {
    if (!schema) return;
    setPhase({ kind: 'computing' });
    setGlobalError(undefined);
    abortRef.current?.abort();
    const ctrl = new AbortController();
    abortRef.current = ctrl;
    try {
      const resp = await client.compute(
        token,
        {
          inputs: orgInputs,
          submitter: {
            full_name: orgMeta.contact_name,
            email: orgMeta.contact_email,
            consent: true,
          },
          locale: locale ?? schema.locale_default,
        },
        ctrl.signal,
      );
      setResult(resp);
      setSubmitter({
        full_name: orgMeta.contact_name,
        email: orgMeta.contact_email,
        consent: true,
      });
      setPhase({ kind: 'result' });
    } catch (err) {
      if ((err as Error).name === 'AbortError') return;
      const msg =
        err instanceof ApiCallError
          ? err.code === 'validation'
            ? t(i18n, 'errors.validation')
            : err.message
          : t(i18n, 'errors.network_error');
      setGlobalError(msg);
      setPhase({ kind: 'org_step_4' });
    }
  }, [client, orgInputs, orgMeta, locale, schema, token, i18n]);

  // Captura opt-in pós-resultado (inline). Best-effort — falha não bloqueia UX.
  const submitLeadOptIn = useCallback(
    async (s: SubmitterInput) => {
      if (!result) return;
      try {
        await client.submitResultSubmitter(token, result.result_id, {
          full_name: s.full_name,
          email: s.email,
          consent: s.consent,
          locale: locale ?? schema?.locale_default,
        });
        setSubmitter(s);
      } catch {
        // Silencioso: o user já viu o resultado, opt-in é bonus.
      }
    },
    [client, locale, result, schema, token],
  );

  const handleCheckout = useCallback(
    async (data: { customerEmail: string; customerName?: string; tco2eToBuy: number }) => {
      if (!result || phase.kind !== 'checkout_confirm') return;
      const offer = phase.offer;
      setGlobalError(undefined);
      setPhase({ kind: 'checkout_redirecting' });
      try {
        const resp = await client.checkout(token, {
          result_id: result.result_id,
          vintage_id: offer.vintage_id,
          tco2e_to_buy: data.tco2eToBuy,
          customer_email: data.customerEmail,
          customer_name: data.customerName,
          return_url: window.location.href,
          cancel_url: window.location.href,
          locale: locale ?? schema?.locale_default,
        });
        // Abre iframe modal — pessoa fica no widget.
        setPhase({
          kind: 'checkout_iframe',
          sessionUrl: resp.session_url,
          orderId: resp.payment_order_id,
          tco2e: data.tco2eToBuy,
          offer,
        });
      } catch (err) {
        const msg =
          err instanceof ApiCallError
            ? err.code === 'insufficient_stock'
              ? t(i18n, 'errors.stock_insufficient')
              : err.code === 'purchase_disabled'
                ? t(i18n, 'errors.purchase_disabled')
                : err.message // backend já mandou em PT-BR
            : t(i18n, 'errors.network_error');
        setGlobalError(msg);
        if (phase.kind === 'checkout_confirm') setPhase(phase);
      }
    },
    [client, locale, phase, result, schema, token, i18n],
  );

  // ── Render ───────────────────────────────────────────────────────────
  if (phase.kind === 'booting') {
    return (
      <WidgetShell
        mode="calc_only"
        title="Carregando calculadora…"
        progress={0}
        fullScreen={fullScreen}
      >
        <div className="py-10 flex justify-center">
          <div
            className="size-8 rounded-full border-2 border-floresta/20 border-t-floresta animate-spin"
            aria-label="Carregando"
          />
        </div>
      </WidgetShell>
    );
  }

  if (phase.kind === 'unavailable') {
    return (
      <WidgetShell
        mode="calc_only"
        title="Calculadora indisponível"
        progress={0}
        fullScreen={fullScreen}
      >
        <p className="text-sm text-ink py-4">{phase.message}</p>
      </WidgetShell>
    );
  }

  // A partir daqui temos schema.
  if (!schema) return null;

  const mode = schema.mode;
  const themeImage = schema.theme_image_url;
  const canPurchase = schema.purchase_mode === 'lead_and_purchase';

  const titleByPhase = (() => {
    switch (phase.kind) {
      case 'step_1':
        return 'Como você foi ao evento?';
      case 'step_2':
        return 'Sua permanência no evento';
      case 'org_step_1':
        return t(i18n, 'org.title.step1');
      case 'org_step_2':
        return t(i18n, 'org.title.step2');
      case 'org_step_3':
        return t(i18n, 'org.title.step3');
      case 'org_step_4':
        return t(i18n, 'org.title.step4');
      case 'computing':
        return 'Calculando sua emissão…';
      case 'result':
        return 'Sua emissão de carbono';
      case 'checkout_confirm':
        return 'Compensar com projetos certificados';
      case 'checkout_redirecting':
        return 'Preparando pagamento…';
      case 'checkout_iframe':
        return 'Compensar com projetos certificados';
      case 'checkout_paid':
        return 'Compensação confirmada';
      default:
        return '';
    }
  })();

  // Step counters: participante=2 (mockup 15), organização=4 (mockup 20).
  const stepInfo = (() => {
    switch (phase.kind) {
      case 'step_1':
        return { current: 1, total: 2 };
      case 'step_2':
        return { current: 2, total: 2 };
      case 'org_step_1':
        return { current: 1, total: 4 };
      case 'org_step_2':
        return { current: 2, total: 4 };
      case 'org_step_3':
        return { current: 3, total: 4 };
      case 'org_step_4':
        return { current: 4, total: 4 };
      default:
        return undefined;
    }
  })();

  const progress = (() => {
    switch (phase.kind) {
      case 'step_1':
        return 0.33;
      case 'step_2':
        return 0.66;
      case 'org_step_1':
        return 0.2;
      case 'org_step_2':
        return 0.4;
      case 'org_step_3':
        return 0.6;
      case 'org_step_4':
        return 0.8;
      case 'computing':
        return 0.9;
      case 'result':
      case 'checkout_confirm':
      case 'checkout_iframe':
      case 'checkout_paid':
      case 'checkout_redirecting':
        return 1;
      default:
        return 0;
    }
  })();

  return (
    <WidgetShell
      mode={mode}
      themeImageUrl={themeImage}
      title={titleByPhase}
      step={stepInfo}
      progress={progress}
      fullScreen={fullScreen}
    >
      {globalError && (
        <div
          role="alert"
          className="mb-4 p-3 rounded-md bg-cobre/10 border border-cobre/30 text-xs text-cobre-700"
        >
          {globalError}
        </div>
      )}

      {phase.kind === 'step_1' && (
        <StepDeslocamento
          value={inputs}
          onChange={patchInputs}
          onNext={() => validateStep1() && setPhase({ kind: 'step_2' })}
          errors={errors}
          eventCity={schema.event_city}
          client={client}
          token={token}
        />
      )}

      {phase.kind === 'step_2' && (
        <StepHospedagem
          value={inputs}
          onChange={patchInputs}
          onBack={() => setPhase({ kind: 'step_1' })}
          onNext={() => {
            if (validateStep2()) runCompute();
          }}
          errors={errors}
        />
      )}

      {phase.kind === 'org_step_1' && (
        <StepOrgEquipePublico
          value={orgInputs}
          onChange={patchOrgInputs}
          onNext={() => setPhase({ kind: 'org_step_2' })}
          i18n={i18n}
        />
      )}

      {phase.kind === 'org_step_2' && (
        <StepOrgDeslocamento
          value={orgInputs}
          onChange={patchOrgInputs}
          onBack={() => setPhase({ kind: 'org_step_1' })}
          onNext={() => setPhase({ kind: 'org_step_3' })}
          i18n={i18n}
        />
      )}

      {phase.kind === 'org_step_3' && (
        <StepOrgOutros
          value={orgInputs}
          onChange={patchOrgInputs}
          onBack={() => setPhase({ kind: 'org_step_2' })}
          onNext={() => setPhase({ kind: 'org_step_4' })}
          i18n={i18n}
        />
      )}

      {phase.kind === 'org_step_4' && (
        <StepOrgInfoEvento
          value={orgMeta}
          onChange={patchOrgMeta}
          onBack={() => setPhase({ kind: 'org_step_3' })}
          onSubmit={runOrgCompute}
          i18n={i18n}
        />
      )}

      {phase.kind === 'computing' && (
        <div className="py-10 flex flex-col items-center gap-3">
          <div
            className="size-8 rounded-full border-2 border-floresta/20 border-t-floresta animate-spin"
            aria-label="Calculando"
          />
          <p className="text-xs text-tabaco">Aplicando fatores GHG-BR · GHG Protocol</p>
        </div>
      )}

      {phase.kind === 'result' && result && (
        <ResultView
          result={result}
          canPurchase={canPurchase}
          onCompensate={(offer) => setPhase({ kind: 'checkout_confirm', offer })}
          onLeadOptIn={submitLeadOptIn}
          leadCaptured={!!submitter}
          onRestart={resetAll}
          i18n={i18n}
          audience={schema.audience}
        />
      )}

      {phase.kind === 'checkout_confirm' && result && (
        <CheckoutConfirm
          result={result}
          offer={phase.offer}
          defaultEmail={submitter?.email}
          defaultName={submitter?.full_name}
          loading={false}
          onCancel={() => setPhase({ kind: 'result' })}
          onConfirm={handleCheckout}
          i18n={i18n}
        />
      )}

      {phase.kind === 'checkout_redirecting' && (
        <div className="py-10 flex flex-col items-center gap-3">
          <div
            className="size-8 rounded-full border-2 border-cobre/20 border-t-cobre animate-spin"
            aria-label="Preparando"
          />
          <p className="text-xs text-tabaco">Conectando ao ambiente de pagamento…</p>
        </div>
      )}

      {phase.kind === 'checkout_paid' && (
        <CheckoutPaidView
          tco2e={phase.tco2e}
          projectName={phase.offer.project_name}
          vintageYear={phase.offer.vintage_year}
          onReset={resetAll}
        />
      )}

      {/* Iframe modal — overlay sobre o widget. Renderiza fora do
        WidgetShell pra ficar position:fixed cobrindo a tela. */}
      {phase.kind === 'checkout_iframe' && (
        <CheckoutIframeModal
          client={client}
          sessionUrl={phase.sessionUrl}
          orderId={phase.orderId}
          onPaid={() =>
            setPhase({
              kind: 'checkout_paid',
              orderId: phase.orderId,
              tco2e: phase.tco2e,
              offer: phase.offer,
            })
          }
          onFailed={(reason) => {
            setGlobalError(
              reason === 'expired'
                ? 'O pagamento expirou. Tente novamente.'
                : reason === 'cancelled'
                  ? 'Pagamento cancelado.'
                  : reason === 'timeout'
                    ? 'Pagamento demorou demais. Verifique seu extrato e tente novamente.'
                    : 'Não foi possível concluir o pagamento.',
            );
            setPhase({ kind: 'checkout_confirm', offer: phase.offer });
          }}
          onCancel={() => setPhase({ kind: 'checkout_confirm', offer: phase.offer })}
        />
      )}
    </WidgetShell>
  );
}

// CheckoutPaidView é a tela de sucesso pós-pagamento. Mostra o resumo
// + um countdown de 30s pra auto-reset (UX de quiosque em evento — se
// ninguém interagir, calculadora volta limpa pro próximo). Qualquer
// movimento de mouse/touch/key pausa o timer; botão "Nova calculadora"
// faz reset imediato.
const AUTO_RESET_SECONDS = 30;

function CheckoutPaidView({
  tco2e,
  projectName,
  vintageYear,
  onReset,
}: {
  tco2e: number;
  projectName: string;
  vintageYear: number;
  onReset: () => void;
}) {
  const [remaining, setRemaining] = useState(AUTO_RESET_SECONDS);
  const [paused, setPaused] = useState(false);

  // Tick a cada 1s. Pausa quando paused=true.
  useEffect(() => {
    if (paused) return;
    if (remaining <= 0) {
      onReset();
      return;
    }
    const t = setTimeout(() => setRemaining((r) => r - 1), 1000);
    return () => clearTimeout(t);
  }, [remaining, paused, onReset]);

  // Qualquer interação pausa o auto-reset — user provavelmente quer ler.
  useEffect(() => {
    const pause = () => setPaused(true);
    const events = ['mousemove', 'touchstart', 'keydown'] as const;
    events.forEach((e) => window.addEventListener(e, pause, { passive: true, once: true }));
    return () => {
      events.forEach((e) => window.removeEventListener(e, pause));
    };
  }, []);

  return (
    <div className="py-10 flex flex-col items-center gap-4 text-center">
      <div
        className="size-16 rounded-full bg-floresta/15 flex items-center justify-center text-floresta text-3xl"
        aria-hidden
      >
        ✓
      </div>
      <div>
        <p className="text-lg font-display text-floresta">
          {tco2e} tCO₂e compensados
        </p>
        <p className="text-sm text-tabaco mt-1">
          {projectName} · vintage {vintageYear}
        </p>
      </div>
      <p className="text-xs text-tabaco max-w-sm">
        Você receberá em instantes o certificado por e-mail.
      </p>
      <button
        type="button"
        onClick={onReset}
        className="mt-2 px-5 py-2.5 rounded-md bg-floresta text-cream text-sm font-medium hover:bg-floresta/90 transition-colors"
      >
        Nova calculadora
      </button>
      {!paused && (
        <p className="text-2xs text-tabaco" aria-live="polite">
          Reiniciando em {remaining}s…
        </p>
      )}
    </div>
  );
}
